import { useState, useRef } from 'react';
import {
  Box, Container, Grid, Chip, Stack, Typography, CircularProgress,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import PageHero from '../components/common/PageHero';
import { Link as RouterLink } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useProjects } from '../hooks/usePublicApi';
import { getProjectImageUrl } from '../utils/imageUrl';
import { projects as staticProjects, projectCategories } from '../data/projects';

const navy  = '#0F1D2E';
const navyL = '#1B2D42';
const gold  = '#D1BC8A';
const goldD = '#B8A06E';

// ── Blueprint overlay ─────────────────────────────────────────────────────────
function Blueprint({ opacity = 0.055 }) {
  return (
    <Box sx={{
      position: 'absolute', inset: 0, pointerEvents: 'none',
      backgroundImage: `
        linear-gradient(rgba(255,255,255,${opacity}) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,${opacity}) 1px, transparent 1px)
      `,
      backgroundSize: '36px 36px',
    }} />
  );
}

// ── Scroll-triggered fade-up ──────────────────────────────────────────────────
function InView({ children, delay = 0, style }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-70px 0px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.62, delay, ease: [0.22, 1, 0.36, 1] }}
      style={{ height: '100%', ...style }}
    >
      {children}
    </motion.div>
  );
}

// ── Project card tile ─────────────────────────────────────────────────────────
function ProjectTile({ project, height = 360, large = false }) {
  const imageUrl = getProjectImageUrl(project);
  const gradient = project.gradient || `linear-gradient(135deg, ${navyL} 0%, #2A4562 100%)`;

  return (
    <motion.div
      whileHover={{ scale: 1.012, y: -4 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      style={{ height: '100%' }}
    >
      <Box
        component={RouterLink}
        to={`/referenzen/${project.slug || project.id}`}
        sx={{
          display: 'block',
          textDecoration: 'none',
          borderRadius: '20px',
          overflow: 'hidden',
          background: imageUrl
            ? `url(${imageUrl}) center/cover no-repeat`
            : gradient,
          height,
          position: 'relative',
          boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
          '&:hover .tile-overlay': { opacity: 1 },
          '&:hover .tile-arrow': { transform: 'translate(3px, -3px)', opacity: 1 },
        }}
      >
        <Blueprint />
        {/* diagonal light sweep */}
        <Box sx={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'linear-gradient(120deg, transparent 40%, rgba(255,255,255,0.04) 50%, transparent 60%)',
        }} />
        {/* bottom scrim */}
        <Box sx={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: '65%',
          background: 'linear-gradient(to top, rgba(15,29,46,0.93) 0%, transparent 100%)',
        }} />
        {/* hover darken */}
        <Box className="tile-overlay" sx={{
          position: 'absolute', inset: 0,
          background: 'rgba(15,29,46,0.18)',
          opacity: 0, transition: 'opacity 0.4s ease', pointerEvents: 'none',
        }} />

        {/* Category chip */}
        <Box sx={{ position: 'absolute', top: 14, left: 14 }}>
          <Chip
            label={project.category}
            size="small"
            sx={{
              bgcolor: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)',
              color: navy, fontWeight: 700, textTransform: 'capitalize',
              fontSize: '0.71rem', px: 0.5,
              boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
            }}
          />
        </Box>

        {/* Arrow button */}
        <Box
          className="tile-arrow"
          sx={{
            position: 'absolute', top: 14, right: 14,
            width: 34, height: 34, borderRadius: '50%',
            background: `linear-gradient(135deg, ${gold} 0%, ${goldD} 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            opacity: 0, transition: 'all 0.35s ease',
            boxShadow: `0 4px 14px rgba(209,188,138,0.4)`,
          }}
        >
          <ArrowForwardIcon sx={{ fontSize: 15, color: navy }} />
        </Box>

        {/* Text content */}
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: large ? 3 : 2.5 }}>
          <Typography
            sx={{
              color: '#fff', fontWeight: 800,
              fontSize: large ? { xs: '1.25rem', md: '1.5rem' } : '1.05rem',
              lineHeight: 1.22, mb: 0.5,
            }}
          >
            {project.title}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1.5, fontSize: '0.82rem' }}>
            {project.subtitle}
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            {project.location && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <LocationOnIcon sx={{ fontSize: 12, color: `rgba(209,188,138,0.85)` }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.52)' }}>
                  {project.location}
                </Typography>
              </Stack>
            )}
            {project.year && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <CalendarTodayIcon sx={{ fontSize: 11, color: `rgba(209,188,138,0.85)` }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.52)' }}>
                  {project.year}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Box>
      </Box>
    </motion.div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Referenzen() {
  const [filter, setFilter] = useState('all');
  const { projects: apiProjects, loading } = useProjects();
  const projects = apiProjects || staticProjects;

  const filtered = filter === 'all' ? projects : projects.filter((p) => p.category === filter);

  return (
    <>
      <Helmet>
        <title>Referenzen — Unsere Projekte | Süd Ausbau GmbH</title>
        <meta name="description" content="Entdecken Sie unsere erfolgreich abgeschlossenen Projekte in München und Umgebung — Neubau, Umbau und Sanierung." />
      </Helmet>

      <PageHero overline="Referenzen" title="Unsere Projekte" />

      {/* ── Filter + project grid ────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: '#F8F7F5', pb: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">

          {/* Category filter pills */}
          <InView style={{ height: 'auto' }}>
            <Stack
              direction="row" spacing={1}
              sx={{ pt: { xs: 5, md: 7 }, pb: { xs: 5, md: 8 }, justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}
            >
              {projectCategories.map((cat) => (
                <motion.div key={cat.value} whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.96 }}>
                  <Chip
                    label={cat.label}
                    onClick={() => setFilter(cat.value)}
                    sx={{
                      px: 1.5,
                      bgcolor: filter === cat.value ? navy : 'transparent',
                      color: filter === cat.value ? gold : 'text.primary',
                      border: '1.5px solid',
                      borderColor: filter === cat.value ? navy : 'rgba(15,29,46,0.18)',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      transition: 'all 0.25s ease',
                      '&:hover': {
                        bgcolor: filter === cat.value ? navyL : 'rgba(15,29,46,0.06)',
                        borderColor: filter === cat.value ? navyL : navy,
                      },
                    }}
                  />
                </motion.div>
              ))}
            </Stack>
          </InView>

          {/* Grid */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 14 }}>
              <CircularProgress sx={{ color: gold }} />
            </Box>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={filter}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.38, ease: [0.25, 0.1, 0.25, 1] }}
              >
                {filtered.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 14 }}>
                    <Typography variant="h6" color="text.secondary">
                      Keine Projekte in dieser Kategorie
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2.5}>
                    {/* Featured large card */}
                    <Grid size={{ xs: 12, md: 7 }}>
                      <InView style={{ height: 'auto' }}>
                        <ProjectTile project={filtered[0]} height={460} large />
                      </InView>
                    </Grid>

                    {/* Second + third stacked */}
                    {filtered.length > 1 && (
                      <Grid size={{ xs: 12, md: 5 }}>
                        <Stack spacing={2.5}>
                          <InView delay={0.1} style={{ height: 'auto' }}>
                            <ProjectTile
                              project={filtered[1]}
                              height={filtered.length > 2 ? 216 : 460}
                            />
                          </InView>
                          {filtered.length > 2 && (
                            <InView delay={0.18} style={{ height: 'auto' }}>
                              <ProjectTile project={filtered[2]} height={216} />
                            </InView>
                          )}
                        </Stack>
                      </Grid>
                    )}

                    {/* Remaining in 3-column row */}
                    {filtered.slice(3).map((project, i) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={project.id || project.slug}>
                        <InView delay={i * 0.08} style={{ height: 'auto' }}>
                          <ProjectTile project={project} height={280} />
                        </InView>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </motion.div>
            </AnimatePresence>
          )}
        </Container>
      </Box>
    </>
  );
}
